/**
 * BHPH Payment Processing Page
 * Handles payment entry, allocation preview, and receipt generation.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from '../../hooks/useDebounce';
import {
  formatCurrency,
  formatDate,
  getBHPHAccount,
  getPaymentSummary,
  getPendingPromises,
  processPayment,
  searchBHPHAccount,
  type PaymentAllocation,
} from '../../services/bhphApi';
import { isUUID } from '../../utils/uuid';
import { shouldResolvePaymentAccountParam } from './paymentProcessingQueryParams';

// ===== Payment Form Schema =====
interface PaymentFormData {
  account_id: string;
  payment_amount: string;
  payment_method: string;
  payment_date?: string;
  notes?: string;
}

const PAYMENT_METHODS = [
  'CASH',
  'CHECK',
  'DEBIT CARD',
  'CREDIT CARD',
  'ZELLE',
  'WIRE',
  'MONEY ORDER',
  'ACH',
] as const;

// ===== Main Component =====
const PaymentProcessingPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);
  const [showAllocationPreview, setShowAllocationPreview] = useState(false);
  const [resolvedAccountParam, setResolvedAccountParam] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<PaymentFormData>({
    defaultValues: {
      payment_method: 'CASH',
      payment_date: new Date().toISOString().split('T')[0],
    },
  });

  const paymentAmount = watch('payment_amount');
  const searchQuery = watch('account_id');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const [selectedAccount, setSelectedAccount] = useState<any>(null);

  // Force cache clear on mount to ensure fresh state
  useEffect(() => {
    queryClient.removeQueries(['bhphAccountSearch']);
  }, [queryClient]);

  // Handle account query param on mount
  useEffect(() => {
    const accountParam = searchParams.get('account');
    if (!shouldResolvePaymentAccountParam(accountParam, resolvedAccountParam)) return;

    setResolvedAccountParam(accountParam);
    setSelectedAccount(null);

    // If UUID, fetch account directly
    if (isUUID(accountParam)) {
      getBHPHAccount(accountParam)
        .then((account) => {
          setSelectedAccount(account);
          setValue(
            'account_id',
            account.deal_number || account.account_id || account.buyer_name || '',
            { shouldDirty: true }
          );
          toast.success(`Account loaded: ${account.buyer_name}`);
        })
        .catch((error) => {
          console.error('Failed to load account from query param:', error);
          toast.error('Unable to load account from link');
        });
    } else {
      // Non-UUID value: prefill search to let existing flow handle it
      setValue('account_id', accountParam, { shouldDirty: true });
    }
  }, [resolvedAccountParam, searchParams, setValue]);

  // Only enable account search if debouncedSearchQuery has at least 3 characters (deal numbers are typically 3+ digits)
  const shouldSearch = !!debouncedSearchQuery && debouncedSearchQuery.trim().length >= 3;

  // Search for account by deal number, account ID, or name
  const {
    data: searchResults,
    isLoading: loadingAccount,
    error: accountError,
  } = useQuery({
    queryKey: ['bhphAccountSearch', debouncedSearchQuery],
    queryFn: () => searchBHPHAccount(debouncedSearchQuery.trim()),
    enabled: shouldSearch,
    retry: 1,
  });

  // Auto-select if only one result
  useEffect(() => {
    if (searchResults && searchResults.length === 1) {
      setSelectedAccount(searchResults[0]);
    } else if (!searchResults || searchResults.length === 0) {
      setSelectedAccount(null);
    }
  }, [searchResults]);

  // Helper helper alias
  const account = selectedAccount;
  // Use the found account's UUID for subsequent queries
  const accountUUID = account?.id;

  // Fetch payment summary
  const { data: _paymentSummary } = useQuery({
    queryKey: ['paymentSummary', accountUUID],
    queryFn: () => getPaymentSummary(accountUUID!),
    enabled: !!accountUUID,
    retry: 1,
  });

  // Fetch pending promises
  const { data: pendingPromises } = useQuery({
    queryKey: ['pendingPromises', accountUUID],
    queryFn: () => getPendingPromises(accountUUID!),
    enabled: !!accountUUID,
    retry: 1,
  });

  // Process payment mutation
  const processPaymentMutation = useMutation({
    mutationFn: processPayment,
    onSuccess: (data) => {
      setReceiptData(data);
      setShowReceipt(true);
      setShowAllocationPreview(false);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['bhphAccountSearch'] });
      queryClient.invalidateQueries({ queryKey: ['paymentSummary', accountUUID] });
      queryClient.invalidateQueries({ queryKey: ['pendingPromises', accountUUID] });
      queryClient.invalidateQueries({ queryKey: ['collectionWorklist'] });
      queryClient.invalidateQueries({ queryKey: ['collectionStats'] });

      // Show success notification
      toast.success(`Payment processed successfully! Receipt #${data.receipt_number}`);
    },
    onError: (error: any) => {
      toast.error(`Error processing payment: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Calculate allocation preview
  const calculateAllocationPreview = (): PaymentAllocation | null => {
    if (!account || !paymentAmount || parseFloat(paymentAmount) <= 0) {
      return null;
    }

    const amount = parseFloat(paymentAmount);
    let remaining = amount;

    // Allocation priority: Late Fees → Interest → Principal → Other → Down
    const late_fees_applied = Math.min(remaining, account.balance_late_amount);
    remaining -= late_fees_applied;

    const interest_applied = Math.min(remaining, account.balance_interest_amount);
    remaining -= interest_applied;

    const principal_applied = Math.min(remaining, account.balance_principal_amount);
    remaining -= principal_applied;

    const other_fees_applied = Math.min(remaining, account.balance_other_amount);
    remaining -= other_fees_applied;

    const down_applied = Math.min(remaining, account.balance_down_amount);
    remaining -= down_applied;

    return {
      total_payment: amount,
      late_fees_applied,
      interest_applied,
      principal_applied,
      other_fees_applied,
      down_applied,
      remaining_unallocated: remaining,
    };
  };

  const allocationPreview = calculateAllocationPreview();

  // Check if payment fulfills a promise
  const fulfilledPromise = pendingPromises?.find((promise: any) => {
    if (!paymentAmount) return false;
    const amount = parseFloat(paymentAmount);
    return amount >= (promise.promise_amount || 0);
  });

  // Submit payment
  const onSubmit = (data: PaymentFormData) => {
    if (!account || !accountUUID) {
      toast.error('Please select a valid account first');
      return;
    }

    if (!confirm('Are you sure you want to process this payment?')) {
      return;
    }

    processPaymentMutation.mutate({
      account_id: accountUUID, // Use the UUID from the found account
      payment_amount: parseFloat(data.payment_amount),
      payment_method: data.payment_method,
      payment_date: data.payment_date,
      notes: data.notes,
    });
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Process Payment</h1>
        <p className="text-gray-600 mt-1">Record a payment and allocate funds to account balance</p>
      </div>

      {/* Receipt View */}
      {showReceipt && receiptData ? (
        <ReceiptView
          receiptData={receiptData}
          onProcessAnother={() => {
            setShowReceipt(false);
            reset();
          }}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Payment Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Details</h2>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <>
                  {/* Account Search/Select */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deal #, Account #, or Customer Name *
                    </label>
                    <input
                      type="text"
                      {...register('account_id', {
                        required: 'Deal number, account ID, or name is required',
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter deal #, account ID, or customer name (min 3 chars)"
                    />
                    {errors.account_id && (
                      <p className="text-red-600 text-sm mt-1">{errors.account_id.message}</p>
                    )}

                    {/* Search Results Selection */}
                    {searchResults && searchResults.length > 1 && (
                      <div className="mt-2 border rounded-md divide-y max-h-60 overflow-y-auto bg-white shadow-sm">
                        <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 border-b">
                          Matches Found ({searchResults.length}) - Please Select Account
                        </div>
                        {searchResults.map((result: any) => (
                          <div
                            key={result.id}
                            className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selectedAccount?.id === result.id ? 'bg-blue-100 border-l-4 border-blue-500' : ''}`}
                            onClick={() => setSelectedAccount(result)}
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-gray-800">{result.buyer_name}</span>
                              <span className="text-sm font-mono text-gray-500">
                                #{result.deal_number}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 truncate mt-1">
                              {result.vehicle_year_make_model || 'Vehicle Info N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Account Info Display */}
                  {loadingAccount && shouldSearch && (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <p className="text-gray-600 text-sm mt-2">Searching for account...</p>
                    </div>
                  )}

                  {accountError && shouldSearch && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      {(accountError as any)?.response?.data?.detail ||
                      (accountError as any)?.response?.data?.error?.message ? (
                        <>
                          <p className="text-red-800 font-medium">Unable to Process Account</p>
                          <p className="text-red-600 text-sm mt-1 whitespace-pre-wrap">
                            {(accountError as any).response?.data?.detail ||
                              (accountError as any).response?.data?.error?.message}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-red-800 font-medium">Account not found</p>
                          <p className="text-red-600 text-sm mt-1">
                            No BHPH account found for "{searchQuery}". Please verify the deal
                            number, account ID, or customer name.
                          </p>
                        </>
                      )}
                    </div>
                  )}

                  {!accountError &&
                    shouldSearch &&
                    !loadingAccount &&
                    searchResults &&
                    searchResults.length === 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-yellow-800 font-medium">No BHPH accounts found</p>
                        <p className="text-yellow-700 text-sm mt-1">
                          No BHPH accounts found for "{searchQuery}". Please verify the deal number,
                          account ID, or customer name.
                        </p>
                      </div>
                    )}

                  {account && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{account.buyer_name}</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">Vehicle:</span>
                          <span className="ml-2 font-medium">
                            {account.vehicle_year_make_model}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Deal #:</span>
                          <span className="ml-2 font-medium">{account.deal_number}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Total Balance:</span>
                          <span className="ml-2 font-semibold text-red-600">
                            {formatCurrency(account.total_balance_amount)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Days Past Due:</span>
                          <span
                            className={`ml-2 font-semibold ${account.days_past_due > 0 ? 'text-red-600' : 'text-green-600'}`}
                          >
                            {account.days_past_due}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Payment Amount:</span>
                          <span className="ml-2 font-medium">
                            {formatCurrency(account.payment_amount)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Next Due Date:</span>
                          <span className="ml-2 font-medium">
                            {formatDate(account.payment_due_date)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payment Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Amount *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        {...register('payment_amount', {
                          required: 'Payment amount is required',
                          min: { value: 0.01, message: 'Amount must be greater than 0' },
                        })}
                        className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                    {errors.payment_amount && (
                      <p className="text-red-600 text-sm mt-1">{errors.payment_amount.message}</p>
                    )}

                    {/* Quick Amount Buttons */}
                    {account && (
                      <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() =>
                            setValue('payment_amount', account.payment_amount.toString())
                          }
                          className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                        >
                          Regular Payment ({formatCurrency(account.payment_amount)})
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setValue('payment_amount', account.total_balance_amount.toString())
                          }
                          className="px-3 py-1 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded border border-green-300"
                        >
                          Pay in Full ({formatCurrency(account.total_balance_amount)})
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method *
                    </label>
                    <select
                      {...register('payment_method', { required: 'Payment method is required' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {PAYMENT_METHODS.map((method) => (
                        <option key={method} value={method}>
                          {method}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Payment Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Date
                    </label>
                    <input
                      type="date"
                      {...register('payment_date')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      {...register('notes')}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add any notes about this payment..."
                    />
                  </div>

                  {/* Promise Fulfillment Alert */}
                  {fulfilledPromise && (
                    <div className="bg-green-50 border border-green-300 rounded-lg p-4">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">✅</span>
                        <div>
                          <h4 className="font-semibold text-green-800">
                            Promise Will Be Fulfilled
                          </h4>
                          <p className="text-sm text-green-700">
                            This payment will fulfill the promise of{' '}
                            {formatCurrency(fulfilledPromise.promise_amount || 0)} due on{' '}
                            {formatDate(fulfilledPromise.promise_date)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowAllocationPreview(!showAllocationPreview)}
                      disabled={!account || !paymentAmount}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-lg transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {showAllocationPreview ? 'Hide' : 'Preview'} Allocation
                    </button>
                    <button
                      type="submit"
                      disabled={processPaymentMutation.isPending || !account}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processPaymentMutation.isPending ? 'Processing...' : 'Process Payment'}
                    </button>
                  </div>
                </>
              </form>
            </div>
          </div>

          {/* Right Column: Allocation Preview & Account Summary */}
          <div className="space-y-6">
            {/* Balance Breakdown */}
            {account && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Balance</h3>
                <div className="space-y-3">
                  <BalanceRow
                    label="Late Fees"
                    amount={account.balance_late_amount}
                    color="text-red-600"
                  />
                  <BalanceRow
                    label="Interest"
                    amount={account.balance_interest_amount}
                    color="text-orange-600"
                  />
                  <BalanceRow
                    label="Principal"
                    amount={account.balance_principal_amount}
                    color="text-blue-600"
                  />
                  <BalanceRow
                    label="Other Fees"
                    amount={account.balance_other_amount}
                    color="text-purple-600"
                  />
                  <BalanceRow
                    label="Down Payment"
                    amount={account.balance_down_amount}
                    color="text-gray-600"
                  />
                  <div className="pt-3 border-t border-gray-200">
                    <BalanceRow
                      label="Total Balance"
                      amount={account.total_balance_amount}
                      color="text-gray-900 font-bold"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Allocation Preview */}
            {showAllocationPreview && allocationPreview && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Allocation Preview</h3>
                <div className="space-y-3">
                  <AllocationRow
                    label="Payment Amount"
                    amount={allocationPreview.total_payment}
                    bold
                  />
                  <div className="border-t border-gray-200 pt-3 space-y-2">
                    {allocationPreview.late_fees_applied > 0 && (
                      <AllocationRow
                        label="→ Late Fees"
                        amount={allocationPreview.late_fees_applied}
                        color="text-red-600"
                      />
                    )}
                    {allocationPreview.interest_applied > 0 && (
                      <AllocationRow
                        label="→ Interest"
                        amount={allocationPreview.interest_applied}
                        color="text-orange-600"
                      />
                    )}
                    {allocationPreview.principal_applied > 0 && (
                      <AllocationRow
                        label="→ Principal"
                        amount={allocationPreview.principal_applied}
                        color="text-blue-600"
                      />
                    )}
                    {allocationPreview.other_fees_applied > 0 && (
                      <AllocationRow
                        label="→ Other Fees"
                        amount={allocationPreview.other_fees_applied}
                        color="text-purple-600"
                      />
                    )}
                    {allocationPreview.down_applied > 0 && (
                      <AllocationRow
                        label="→ Down Payment"
                        amount={allocationPreview.down_applied}
                        color="text-gray-600"
                      />
                    )}
                  </div>
                  {allocationPreview.remaining_unallocated > 0 && (
                    <div className="pt-2 border-t border-gray-200">
                      <AllocationRow
                        label="Overpayment (Credit)"
                        amount={allocationPreview.remaining_unallocated}
                        color="text-green-600"
                      />
                    </div>
                  )}
                  <div className="pt-3 border-t-2 border-gray-300">
                    <AllocationRow
                      label="New Balance"
                      amount={Math.max(
                        0,
                        (account?.total_balance_amount || 0) - allocationPreview.total_payment
                      )}
                      color="text-gray-900 font-bold"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Pending Promises */}
            {pendingPromises && pendingPromises.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">Pending Promises</h4>
                <div className="space-y-2">
                  {pendingPromises.map((promise: any) => (
                    <div key={promise.id} className="text-sm">
                      <div className="flex justify-between">
                        <span className="text-yellow-700">Amount:</span>
                        <span className="font-medium">
                          {formatCurrency(promise.promise_amount || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-700">Due:</span>
                        <span className="font-medium">{formatDate(promise.promise_date)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Component: Balance Row =====
interface BalanceRowProps {
  label: string;
  amount: number;
  color?: string;
}

const BalanceRow: React.FC<BalanceRowProps> = ({ label, amount, color = 'text-gray-900' }) => {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">{label}:</span>
      <span className={color}>{formatCurrency(amount)}</span>
    </div>
  );
};

// ===== Component: Allocation Row =====
interface AllocationRowProps {
  label: string;
  amount: number;
  color?: string;
  bold?: boolean;
}

const AllocationRow: React.FC<AllocationRowProps> = ({
  label,
  amount,
  color = 'text-gray-700',
  bold = false,
}) => {
  return (
    <div className="flex justify-between text-sm">
      <span className={`${color} ${bold ? 'font-bold' : ''}`}>{label}:</span>
      <span className={`${color} ${bold ? 'font-bold' : ''}`}>{formatCurrency(amount)}</span>
    </div>
  );
};

// ===== Component: Receipt View =====
interface ReceiptViewProps {
  receiptData: any;
  onProcessAnother: () => void;
}

const ReceiptView: React.FC<ReceiptViewProps> = ({ receiptData, onProcessAnother }) => {
  const navigate = useNavigate();

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Receipt Header */}
        <div className="text-center mb-6 pb-6 border-b-2 border-gray-300">
          <div className="text-4xl mb-3">✅</div>
          <h2 className="text-2xl font-bold text-green-600 mb-2">Payment Processed Successfully</h2>
          <p className="text-gray-600">Receipt #{receiptData.receipt_number}</p>
        </div>

        {/* Receipt Details */}
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-600">Payment Date:</span>
              <p className="font-medium">{formatDate(receiptData.payment_date)}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Payment Method:</span>
              <p className="font-medium">{receiptData.payment_method}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Account ID:</span>
              <p className="font-medium">{receiptData.account_id}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Processed By:</span>
              <p className="font-medium">{receiptData.processed_by}</p>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-center">
              <span className="text-sm text-gray-600">Payment Amount</span>
              <p className="text-3xl font-bold text-blue-600">
                {formatCurrency(receiptData.payment_amount)}
              </p>
            </div>
          </div>

          {/* Allocation Breakdown */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-900 mb-3">Payment Allocation</h3>
            <div className="space-y-2">
              {receiptData.allocation.late_fees_applied > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Late Fees:</span>
                  <span className="font-medium text-red-600">
                    {formatCurrency(receiptData.allocation.late_fees_applied)}
                  </span>
                </div>
              )}
              {receiptData.allocation.interest_applied > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Interest:</span>
                  <span className="font-medium text-orange-600">
                    {formatCurrency(receiptData.allocation.interest_applied)}
                  </span>
                </div>
              )}
              {receiptData.allocation.principal_applied > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Principal:</span>
                  <span className="font-medium text-blue-600">
                    {formatCurrency(receiptData.allocation.principal_applied)}
                  </span>
                </div>
              )}
              {receiptData.allocation.other_fees_applied > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Other Fees:</span>
                  <span className="font-medium text-purple-600">
                    {formatCurrency(receiptData.allocation.other_fees_applied)}
                  </span>
                </div>
              )}
              {receiptData.allocation.down_applied > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Down Payment:</span>
                  <span className="font-medium text-gray-600">
                    {formatCurrency(receiptData.allocation.down_applied)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Balance Update */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Previous Balance:</span>
              <span className="font-medium">{formatCurrency(receiptData.balance_before)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">New Balance:</span>
              <span className="font-bold text-green-600">
                {formatCurrency(receiptData.new_balance)}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-6 border-t border-gray-200">
          <button
            onClick={() => window.print()}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-lg transition duration-150"
          >
            🖨️ Print Receipt
          </button>
          <button
            onClick={onProcessAnother}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-150"
          >
            Process Another Payment
          </button>
        </div>

        <div className="mt-3">
          <button
            onClick={() => navigate('/bhph/dashboard')}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-150"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessingPage;
